export class KeyValueObservable<T> {
  private observables: Map<string, {value: T, observers: ((value: T, oldValue: T) => void)[]}>;

  constructor() {
    this.observables = new Map();
  }

  _checkCreateObservable(key: string, value: T) {
    if (!this.observables.has(key)) {
      this.observables.set(key, {
        value: value,
        observers: []
      });
    }
  }

  // Notify all subscribers of the state change
  _notifyObservers(key: string, oldValue: T) {
    if (this.observables.has(key)) {
      const observable = this.observables.get(key);
      if (observable && observable.observers) {
        for (const observer of observable.observers) {
          observer(observable.value, oldValue);
        }
      }
    }
  }

  notify(key: string) {
    this._notifyObservers(key,  undefined as unknown as T);
  }

  subscribe(key: string, observer: (value: T, oldValue: T) => void) {
    this._checkCreateObservable(key, undefined as unknown as T);
    this.observables.get(key)?.observers?.push(observer);
  }

  unsubscribe(key: string, observer: (value: T, oldValue: T) => void) {
    if (this.observables.has(key)) {
      const observable = this.observables.get(key);
      if (!observable) return;
      const observers: ((value: T, oldValue: T) => void)[] = observable.observers;
      if (observers?.length) {
        observable.observers = observers.filter((storedObserver: (value: T, oldValue: T) => void) => storedObserver !== observer);
      }
    }
  }

  // Set a new key-value pair
  set(key: string, value: T) {
    this._checkCreateObservable(key, undefined as unknown as T);
    const observable = this.observables.get(key);
    if (observable && observable.observers) {
      const oldValue = observable.value;
      observable.value = value;
      this._notifyObservers(key, oldValue);
    }
  }

  // Delete a key-value pair
  delete(key: string) {
    if (this.observables.has(key)) {
      const observable = this.observables.get(key);
      if (observable && observable.observers) {
        observable.observers.length = 0;
      }
      this.observables.delete(key);
    }
  }

  // Get the current value for a key
  get(key: string) {
    return this.observables.get(key)?.value;
  }
}